import NiceModal from "@ebay/nice-modal-react";
import { bffRequester, requester } from "App";
import Dummy from "components/Dummy";

import axios from "axios";
import P from "components/P";
import CustomButton from "components/buttons/CustomButton";
import InputText from "components/inputs/signUp/InputText";
import FlexChild from "layouts/flex/FlexChild";
import VerticalFlex from "layouts/flex/VerticalFlex";
import Center from "layouts/wrapper/Center";
import ModalBase from "modals/base/ModalBase";
import { AuthContext } from "providers/AuthProvider";
import { BrowserDetectContext } from "providers/BrowserEventProvider";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom/dist";
import { toast } from "react-toastify";
import { AuthReducer } from "shared/redux/reducers/auth/AuthReducer";
import { HistoryReducer } from "shared/redux/reducers/history/HistoryReducer";
import { decode, getTokenPayload, isAdult } from "shared/utils/Utils";
import { devLog } from "utils/logger";

const NumberDoorVerificationModal = NiceModal.create(props => {
    const { t } = useTranslation();
    const [withHeader, withFooter] = [false, false];
    const [width, height] = ["100%", "60%"];
    const withCloseButton = true;
    const modal = useRef();
    const clickOutsideToClose = false;
    const title = t("");
    const buttonText = t("close");
    const navigate = useNavigate();
    const authNumberRef = useRef();
    const { isMobile } = useContext(BrowserDetectContext);
    const { userId, userName } = useContext(AuthContext)
    const dispatch = useDispatch();
    const clickCountRef = useRef(0);
    const [iotLoading, setIotLoading] = useState(false);
    const [statusMessage, setStatusMessage] = useState("");

    // props 디버깅용
    useEffect(() => {
        devLog("[NumberDoorVerificationModal] 모달 props:", {
            deviceCode: props.deviceCode,
            iotId: props.iotId,
            storeCode: props.storeCode,
            data: props.data,
            token: props.token,
            type: props.type,
            full: props
        });
    }, [props]);

    // 웹뷰 체크 함수
    const isOurAppWebView = () => {
        const userAgent = navigator.userAgent.toLowerCase();

        // 웹뷰 감지 로직
        const isWebView = (() => {
            const isIOSWebView = /(iphone|ipod|ipad).*applewebkit(?!.*safari)/i.test(userAgent);
            const isAndroidWebView = /wv/.test(userAgent) || /android.*samsungbrowser\/(?!.*chrome)/i.test(userAgent);

            return isIOSWebView || isAndroidWebView;
        })();

        // 웹뷰 확인
        if (isWebView) {
            if (window.BRIDGE && window.BRIDGE.isOurAppWebView && window.BRIDGE.isOurAppWebView()) {
                return true;
            } else if (window.webkit?.messageHandlers?.BRIDGE) {
                return true;
            }
        }

        return false;
    };

    // 창 닫기 함수
    const closeWebView = () => {
        const userAgent = navigator.userAgent.toLowerCase();

        // 웹뷰인 경우
        if (isOurAppWebView()) {
            if (window.BRIDGE && typeof window.BRIDGE.closeWebView === "function") {
                window.BRIDGE.closeWebView();
            } else if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.BRIDGE) {
                window.webkit.messageHandlers.BRIDGE.postMessage('closeWebView');
            }
        } else {
            // 웹뷰가 아닌 경우
            try {
                window.close();
            } catch (error) {
                devLog('브라우저에서 창을 닫을 수 없습니다.');
            }
        }
    };

    // 문 열기 함수 - 직접 Tuya API 호출
    const openDoorById = async (deviceId, token, tokenData) => {
        if (!deviceId) {
            toast.error("디바이스 ID가 필요합니다.");
            return false;
        }

        // 토큰 확인
        if (!token) {
            toast.error("토큰이 없습니다.");
            return false;
        }

        setIotLoading(true);
        setStatusMessage("문 열기 요청 중...");

        try {
            // 토큰의 첫 두 글자를 기반으로 지역 결정
            const region = token.substring(0, 2);

            // 지역별 URL 결정
            const baseUrlMap = {
                "EU": "https://px1.tuyaeu.com/homeassistant/",
                "AY": "https://px1.tuyacn.com/homeassistant/",
                "AZ": "https://px1.tuyaus.com/homeassistant/"
            };

            const baseUrl = baseUrlMap[region] || "https://px1.tuyaeu.com/homeassistant/";
            const url = `${baseUrl}skill`;

            devLog(`지역 감지: ${region}, API URL: ${url}`);

            // 요청 데이터 구성
            const data = {
                header: {
                    name: "turnOnOff",
                    namespace: "control",
                    payloadVersion: 1,
                },
                payload: {
                    accessToken: token,
                    devId: deviceId,
                    value: 1  // 1: 켜기(열기)
                },
            };

            devLog(`문 열기 요청 데이터:`, JSON.stringify(data));

            // 요청 전송
            const response = await axios.post(url, data, {
                headers: { "Content-Type": "application/json" }
            });

            devLog("문 열기 응답:", response.data);
            setIotLoading(false);

            if (response.data && response.data.header && response.data.header.code === "SUCCESS") {
                toast.success("문이 열렸습니다! 잠시 후 닫힙니다.", {
                    autoClose: 3000, position: toast.POSITION.BOTTOM_CENTER
                });

                let storeEntryAuthCreateData = {
                    storeCode: props.storeCode,
                    userId: getTokenPayload(tokenData).userId
                }

                requester.storeEntryAuthCreate(storeEntryAuthCreateData, (result) => {
                    if (result.code === 0) {
                        // 성공 시 모달 닫고 출입문 성공 페이지로 이동
                        modal.current.close();

                        // 웹뷰인 경우와 아닌 경우 구분 처리
                        if (isOurAppWebView()) {
                            // 1.5초 후 출입문 성공 페이지로 이동
                            setTimeout(() => {
                                navigate(`/ws/${props.storeCode}/door-success`, {
                                    state: {
                                        doorOpened: true,
                                        deviceId: deviceId,
                                        timestamp: new Date().toISOString()
                                    },
                                    replace: true // history 스택에서 이전 페이지 대체 (뒤로가기 방지)
                                });
                            }, 1500);

                            // 5초 후 웹뷰 닫기
                            setTimeout(() => {
                                closeWebView();
                            }, 5000);
                        } else {
                            // 일반 브라우저인 경우 (웹뷰가 아닌 경우)
                            // 1.5초 후 출입문 성공 페이지로 이동
                            setTimeout(() => {
                                navigate(`/ws/${props.storeCode}/door-success`, {
                                    state: {
                                        doorOpened: true,
                                        deviceId: deviceId,
                                        timestamp: new Date().toISOString()
                                    },
                                    replace: true // history 스택에서 이전 페이지 대체 (뒤로가기 방지)
                                });
                            }, 1500);
                        }
                    }
                })

                // // 성공 시 모달 닫고 출입문 성공 페이지로 이동
                // modal.current.close();

                // // 웹뷰인 경우와 아닌 경우 구분 처리
                // if (isOurAppWebView()) {
                //     // 1.5초 후 출입문 성공 페이지로 이동
                //     setTimeout(() => {
                //         navigate(`/ws/${props.storeCode}/door-success`, {
                //             state: {
                //                 doorOpened: true,
                //                 deviceId: deviceId,
                //                 timestamp: new Date().toISOString()
                //             },
                //             replace: true // history 스택에서 이전 페이지 대체 (뒤로가기 방지)
                //         });
                //     }, 1500);

                //     // 5초 후 웹뷰 닫기
                //     setTimeout(() => {
                //         closeWebView();
                //     }, 5000);
                // } else {
                //     // 일반 브라우저인 경우 (웹뷰가 아닌 경우)
                //     // 1.5초 후 출입문 성공 페이지로 이동
                //     setTimeout(() => {
                //         navigate(`/ws/${props.storeCode}/door-success`, {
                //             state: {
                //                 doorOpened: true,
                //                 deviceId: deviceId,
                //                 timestamp: new Date().toISOString()
                //             },
                //             replace: true // history 스택에서 이전 페이지 대체 (뒤로가기 방지)
                //         });
                //     }, 1500);
                // }

                return true;
            } else {
                const errorCode = response.data?.header?.code || "UNKNOWN_ERROR";
                const errorMessage = response.data?.header?.message || "알 수 없는 오류";

                console.error(`문 열기 실패 - 코드: ${errorCode}, 메시지: ${errorMessage}, 전체 응답:`, response.data);

                // 토큰 오류일 경우 토큰 재발급 안내
                if (errorCode === "FrequentlyInvoke") {
                    toast.error(`요청이 너무 빈번합니다. 잠시 후 다시 시도해주세요.`, {
                        autoClose: 3000, position: toast.POSITION.BOTTOM_CENTER
                    });
                } else if (errorCode === "TokenInvalid" || errorCode === "UserSessionTimeOut") {
                    toast.error(`토큰이 만료되었습니다. 새로고침 후 다시 시도해주세요.`, {
                        autoClose: 3000, position: toast.POSITION.BOTTOM_CENTER
                    });
                } else {
                    toast.error(`문 열기 실패: [${errorCode}] ${errorMessage}`, {
                        autoClose: 3000, position: toast.POSITION.BOTTOM_CENTER
                    });
                }

                setIotLoading(false);
                return false;
            }
        } catch (error) {
            console.error("문 열기 오류:", error);
            // 오류 응답이 있는 경우 자세한 정보 출력
            if (error.response) {
                console.error("오류 응답:", error.response.data);
                console.error("오류 상태:", error.response.status);
                toast.error(`문 열기 오류: [${error.response.status}] ${JSON.stringify(error.response.data || {})}`, {
                    autoClose: 3000, position: toast.POSITION.BOTTOM_CENTER
                });
            } else if (error.request) {
                console.error("요청은 보냈으나 응답 없음:", error.request);
                toast.error("서버에서 응답이 없습니다. 네트워크를 확인해주세요.", {
                    autoClose: 3000, position: toast.POSITION.BOTTOM_CENTER
                });
            } else {
                console.error("요청 구성 중 오류:", error.message);
                toast.error(`요청 오류: ${error.message || "알 수 없는 오류"}`, {
                    autoClose: 3000, position: toast.POSITION.BOTTOM_CENTER
                });
            }
            setIotLoading(false);
            return false;
        }
    };

    // 서버에서 Tuya 토큰 가져오기
    const fetchTuyaTokenFromServer = async () => {
        try {
            setIotLoading(true);
            setStatusMessage("서버에서 IoT 토큰 가져오는 중...");

            return new Promise((resolve) => {
                requester.findbyTuyaToken((result) => {
                    devLog("서버 토큰 응답:", result);

                    // 전체 응답을 문자열로 변환하여 표시 (디버깅용)
                    const resultString = JSON.stringify(result, null, 2);
                    devLog("서버 토큰 응답 (문자열):", resultString);

                    if (result.code === 0) {
                        // 토큰 정보를 추출하여 저장
                        const tokenData = result.data;
                        devLog("서버에서 가져온 토큰 정보:", tokenData);

                        // 토큰이 있으면 상태에 저장 (쿠키에 저장하지 않음)
                        if (tokenData.accessToken) {
                            setStatusMessage("IoT 시스템에 연결되었습니다.");
                            setIotLoading(false);
                            resolve(tokenData.accessToken);
                        } else {
                            console.error("서버에서 토큰을 가져왔지만 유효한 토큰이 없습니다.");
                            toast.error("서버에서 유효한 토큰을 찾을 수 없습니다.", {
                                autoClose: 3000, position: toast.POSITION.BOTTOM_CENTER
                            });
                            setIotLoading(false);
                            resolve(null);
                        }
                    } else {
                        console.error("서버에서 토큰 가져오기 실패:", result);
                        toast.error("서버에서 토큰 가져오기 실패", {
                            autoClose: 3000, position: toast.POSITION.BOTTOM_CENTER
                        });
                        setIotLoading(false);
                        resolve(null);
                    }
                });
            });
        } catch (error) {
            console.error("서버 토큰 요청 오류:", error);
            toast.error("서버 토큰 요청 오류: " + (error.message || "알 수 없는 오류"), {
                autoClose: 3000, position: toast.POSITION.BOTTOM_CENTER
            });
            setIotLoading(false);
            return null;
        }
    };

    // IoT 디바이스 ID 디코딩
    const getDecodedDeviceId = () => {
        try {
            // deviceCode와 iotId 둘 다 확인해서 사용
            const deviceId = props.deviceCode || props.iotId;

            if (deviceId) {
                devLog(`[NumberDoorVerificationModal] 디코딩 전 디바이스 ID: ${deviceId}`);
                // const decoded = decode(deviceId);
                // devLog(`[NumberDoorVerificationModal] 디코딩 후 디바이스 ID: ${decoded}`);
                return deviceId;
            }
            devLog("[NumberDoorVerificationModal] 디바이스 ID가 없습니다.");
            return deviceId; // 디코딩할 수 없으면 원본 반환
        } catch (e) {
            console.error("[NumberDoorVerificationModal] 디바이스 ID 디코딩 실패:", e);
            const deviceId = props.deviceCode || props.iotId;
            return deviceId; // 디코딩 실패시 원본값 사용
        }
    };

    // 문 열기 처리 함수
    const handleOpenDoor = async (iottoken, tokenData) => {
        const decodedDeviceId = getDecodedDeviceId();
        devLog("문 열기 시도 - 디바이스 ID:", decodedDeviceId);

        // 토큰이 없는 경우 서버에서 가져오기
        let tokenToUse = iottoken;
        if (!tokenToUse) {
            devLog("토큰이 없어 서버에서 가져옵니다");
            tokenToUse = await fetchTuyaTokenFromServer();
        }

        // 문 열기 시도
        if (tokenToUse && decodedDeviceId) {
            const success = await openDoorById(decodedDeviceId, tokenToUse, tokenData);
            if (success) {
                devLog("문 열기 성공");
            } else {
                devLog("문 열기 실패");
            }
        } else {
            toast.error("문을 열 수 없습니다. 토큰 또는 디바이스 ID가 없습니다.", {
                autoClose: 3000, position: toast.POSITION.BOTTOM_CENTER
            });
        }
    };

    const passOnClick = () => {
        dispatch(HistoryReducer.actions.setLoading(true));
        validate();
    }
    const smsOnClick = () => {

        const outerMOKConfirmData = props.data.MOKConfirmData;
        const innerMOKConfirmData = JSON.parse(outerMOKConfirmData);
        if (innerMOKConfirmData.resultCode === "2000") {
            let confirmdata =
            {
                MOKConfirmData: innerMOKConfirmData,
                session: props.token.session,
                authNumber: authNumberRef.current.getValue()
            };

            bffRequester.apiResult(confirmdata, (result) => {
                if (result.resultData) {
                    let mobileDuplicateData = { mobileNumber: props.info.userPhoneNum, storeId: "root", role: "MEMBER" }
                    requester.membermobileNumDuplicate(mobileDuplicateData, (duplicateResult) => {
                        if (duplicateResult.code === 0) {
                            const isAdultUser = isAdult(props.info.userBirthday);
                            if (duplicateResult.data) { // Active member
                                if (isAdultUser) {
                                    signIn();
                                } else {
                                    alert("성인만 가입할 수 있습니다.");
                                    modal.current?.close();
                                }
                            } else { // Not an active member, check if withdrawn
                                let useYnConfirmData = { userName: props.info.userPhoneNum };
                                requester.useYnConfirm(useYnConfirmData, (useYnResult) => {
                                    if (useYnResult.code === 0 && useYnResult.data) { // Withdrawn member
                                        if (isAdultUser) {
                                            toast.info("탈퇴한 계정입니다. 계정을 복구하고 로그인합니다.", {
                                                autoClose: 1000, position: toast.POSITION.BOTTOM_CENTER
                                            });
                                            let restoreData = { storeId: "root", userName: props.info.userPhoneNum, role: "MEMBER" };
                                            requester.restoreAccount(restoreData, (restoreResult) => {
                                                if (restoreResult.code === 0) {
                                                    signIn();
                                                } else {
                                                    toast.error("계정 복구에 실패했습니다.");
                                                }
                                            });
                                        } else {
                                            alert("성인만 가입할 수 있습니다.");
                                            modal.current?.close();
                                        }
                                    } else { // New member
                                        if (isAdultUser) {
                                            dispatch(HistoryReducer.actions.setLoading(true));
                                            
                                            signUp(result.resultData.ci, result.resultData.di, props.info.userBirthday);
                                            modal.current.close();
                                        } else {
                                            alert("성인만 가입할 수 있습니다.");
                                            modal.current?.close();
                                        }
                                    }
                                });
                            }
                        } else {
                            toast.error("회원 정보 확인 중 오류가 발생했습니다.");
                        }
                    })
                } else {
                    alert("인증번호를 다시 확인해주세요");
                }
            })
        }
    }

    const validate = (birthDay) => {
        let adultYn = false;
        const outerMOKConfirmData = props.data.MOKConfirmData;

        const innerMOKConfirmData = JSON.parse(outerMOKConfirmData);

        let validateData = {
            MOKConfirmData: innerMOKConfirmData,
            session: props.token.session,
            authNumber: props.authNumber ? props.authNumber : '',
            type: props.type,
            info: props.info
        }

        if (props.type === "PASS") {
            bffRequester.apiResult(validateData, (result) => {
                if (result.resultData) {
                    adultYn = isAdult(result.resultData.userBirthday)
                } else {

                }
                let mobileDuplicateData = { mobileNumber: props.info.userPhoneNum, storeId: "root", role: "MEMBER" }
                if (adultYn) {
                    requester.membermobileNumDuplicate(mobileDuplicateData, (duplicateResult) => {
                        if (duplicateResult.code === 0) {
                            if (duplicateResult.data) { // Active member
                                let useYnConfirmData = { userName: props.info.userPhoneNum };
                                requester.useYnConfirm(useYnConfirmData, (useYnResult) => {
                                    if (useYnResult.code === 0 && useYnResult.data) { // Withdrawn member
                                        toast.info("탈퇴한 계정입니다. 계정을 복구하고 로그인합니다.", {
                                            autoClose: 1000, position: toast.POSITION.BOTTOM_CENTER
                                        });
                                        let restoreData = { storeId: "root", userName: props.info.userPhoneNum, role: "MEMBER" };
                                        requester.restoreAccount(restoreData, (restoreResult) => {
                                            if (restoreResult.code === 0) {
                                                signIn();
                                            } else {
                                                toast.error("계정 복구에 실패했습니다.");
                                            }
                                        });
                                    } else { // New member
                                        dispatch(HistoryReducer.actions.setLoading(true));
                                        // toast.success("인증에 성공했습니다. 로그인 후 문을 열겠습니다.", {
                                        //     autoClose: 1000, position: toast.POSITION.BOTTOM_CENTER
                                        // });
                                        signIn();
                                    }
                                });
                            } else { // Not an active member, check if withdrawn
                                let useYnConfirmData = { userName: props.info.userPhoneNum };
                                requester.useYnConfirm(useYnConfirmData, (useYnResult) => {
                                    if (useYnResult.code === 0 && useYnResult.data) { // Withdrawn member
                                        let restoreData = { storeId: "root", userName: props.info.userPhoneNum, role: "MEMBER" };
                                        requester.restoreAccount(restoreData, (restoreResult) => {
                                            if (restoreResult.code === 0) {
                                                signIn();
                                            } else {
                                                toast.error("계정 복구에 실패했습니다.");
                                            }
                                        });
                                    } else { // New member
                                        dispatch(HistoryReducer.actions.setLoading(true));
                                        // toast.success("인증에 성공했습니다. 회원가입을 진행합니다.", {
                                        //     autoClose: 1000, position: toast.POSITION.BOTTOM_CENTER
                                        // });
                                        signUp(result.resultData.ci, result.resultData.di, result.resultData.userBirthday);
                                        modal.current.close();
                                    }
                                });
                            }
                        } else {
                            toast.error("회원 정보 확인 중 오류가 발생했습니다.");
                        }
                    });

                } else {
                    if (result.resultData) {
                        alert("성인이 아닙니다.");
                    } else {
                        clickCountRef.current += 1;
                        if (clickCountRef.current > 3) {
                            alert("처음부터 다시 시도해주세요");
                            // 카운트를 리셋하여 사용자가 처음부터 다시 시도할 수 있게 합니다.
                            clickCountRef.current = 0;
                            // 추가 실행을 막기 위해 함수에서 반환합니다.
                            modal.current.close();
                            return;
                        } else {
                            alert("인증번호를 다시한번 확인해주세요");
                        }
                    }

                }

            })
        } else if (props.type === "SMS") {
            adultYn = isAdult(props.info.userBirthday);
            if (adultYn) {
                // toast.success("인증에 성공했습니다. 문을 열겠습니다.", {
                //     autoClose: 1000, position: toast.POSITION.BOTTOM_CENTER
                // });
                signUp(null, null, birthDay);
                // setMobileNumber(props.info.userPhoneNum);
            } else {
                alert("성인만 가입할 수 있습니다.");
                // navigate("/numberSignUp", { replace: true })
                modal.current.close();
            }

        }

    }

    const signIn = () => {
        let data = { storeId: "root", userName: props.info.userPhoneNum, role: "MEMBER" };

        requester.userNumberSignIn(data, (result) => {

            if (result.code === -1) {
                if (result.message === "Invalid userName") {
                    //존자하지 않는 아이디
                    toast.error(t("noUserName"), {
                        autoClose: 1000, position: toast.POSITION.BOTTOM_CENTER
                    });
                } else if (result.message === "Invalid credentials") {
                    //비번틀림
                    toast.error(t("noPassword"), {
                        autoClose: 1000, position: toast.POSITION.BOTTOM_CENTER
                    });
                } else {
                    toast.error(t("잘못된 연락처"), {
                        autoClose: 1000, position: toast.POSITION.BOTTOM_CENTER
                    });
                }
            } else if (result.code === 0) {
                let tokenData = result.data.token;
                
                dispatch(AuthReducer.actions.setToken(tokenData));
                // 인증 성공 후 문 열기 로직 실행
                devLog("인증이 완료되어 문 열기를 시도합니다.");

                // IoT 토큰 가져와서 문 열기
                fetchTuyaTokenFromServer().then(iotToken => {
                    if (iotToken) {
                        handleOpenDoor(iotToken, tokenData);
                    } else {
                        toast.error("IoT 토큰을 가져올 수 없습니다.", {
                            autoClose: 3000, position: toast.POSITION.BOTTOM_CENTER
                        });
                    }
                });
                // WebView 전송
                setTimeout(() => {
                    if (window.webkit?.messageHandlers?.BRIDGE) {
                        window.webkit.messageHandlers.BRIDGE.postMessage({ command: "setToken", token: tokenData });
                        modal.current?.close();
                    }
                }, 500);

                // Android navigate
                const isAndroid = /Android/i.test(navigator.userAgent);
                if (isAndroid) {
                    // 문 열기가 완료되면 모달 닫기
                    setTimeout(() => {
                        modal.current?.close();
                    }, 4000);
                }
            }
        })
    }

    const signUp = (ci, di, birthDay) => {

        let data = { storeId: "root", name: props.info.userName, mobileNumber: props.info.userPhoneNum, ci: ci, di: di, birthDay: birthDay }

        requester.memberNumberSignUp(data, (result) => {

            if (result.code === 0) {
                signIn()
            } else {
                toast.error(t("문제가 발생했습니다 관리자에게 문의하세요"), {
                    autoClose: 1000, position: toast.POSITION.BOTTOM_CENTER
                });
            }
            dispatch(HistoryReducer.actions.setLoading(false));
        });
    }

    const btnStyle = {
        width: "100%",
        backgroundColor: "var(--main-color)",
        border: "none",
        height: "48px",
        fontWeight: 700,
        color: "white"
    }

    return (
        <ModalBase
            slideUp
            ref={modal}
            width={width}
            height={height}
            withHeader={withHeader}
            withFooter={withFooter}
            withCloseButton={withCloseButton}
            clickOutsideToClose={clickOutsideToClose}
            title={title}
            buttonText={buttonText}
            overflow={"visible"}
            borderRadius={"25px"}
            onClose={props.onClose}
        >
            {
                props.data &&
                    props.type === "SMS"
                    ?
                    <VerticalFlex gap={10} padding={30} backgroundColor={"white"}>

                        <FlexChild height={"initial"}>
                            <Dummy height={"2vh"} />
                            {/* <img src={puffuIcon} width={"40%"} /> */}
                        </FlexChild>
                        <FlexChild height={"initial"}>
                            <Dummy height={"2vh"} />
                            <Center>
                                <P color={"black"} weight={700} size={35}>출입문 SMS 인증</P>
                            </Center>
                        </FlexChild>
                        <FlexChild width={"70%"} height={"initial"}>
                            <InputText ref={authNumberRef} id="authNumber" name="authNumber" placeHolder={"인증번호를 입력하세요."} />
                        </FlexChild>
                        <FlexChild>
                            <Center>
                                <CustomButton style={btnStyle} onClick={smsOnClick} text={"인증확인"} />
                            </Center>
                        </FlexChild>
                    </VerticalFlex>
                    :
                    <VerticalFlex gap={10} padding={30} backgroundColor={"white"}>
                        <FlexChild height={"initial"}>
                            <Dummy height={"2vh"} />
                            {/* <img src={puffuIcon} width={"30%"} /> */}
                        </FlexChild>
                        <FlexChild>
                            <Center>
                                <P color={"black"} weight={700} size={35}>출입문 PASS인증</P>
                                <Dummy height={"10px"} />
                                <P color={"black"} weight={700} size={"4vw"}>PASS인증이 완료되었다면</P>
                                <P size={"4vw"} weight={700} color={"black"}>버튼을 클릭해주세요.</P>
                            </Center>
                        </FlexChild>
                        <FlexChild>
                            <Center>
                                <CustomButton style={btnStyle} onClick={passOnClick} text={"PASS인증완료"} />
                            </Center>
                        </FlexChild>
                        {iotLoading && (
                            <FlexChild>
                                <Center>
                                    <div style={{
                                        border: '3px solid #f3f3f3',
                                        borderTop: '3px solid var(--main-color)',
                                        borderRadius: '50%',
                                        width: '24px',
                                        height: '24px',
                                        animation: 'spin 1s linear infinite'
                                    }}></div>
                                    <style>
                                        {`
                                            @keyframes spin {
                                                0% { transform: rotate(0deg); }
                                                100% { transform: rotate(360deg); }
                                            }
                                        `}
                                    </style>
                                </Center>
                            </FlexChild>
                        )}
                    </VerticalFlex>
            }

        </ModalBase>
    );
});

export default NumberDoorVerificationModal;
